/**
 * ReadinessDashboard — "Ribbon + Cards" redesign.
 *
 * Layout:
 *   Row 1:  Bold % | full-width progress bar | cost badge
 *   Row 2:  State chips | priority badges
 *   Row 3:  Phase pills (horizontal, flex-wrap)
 *   Row 4:  Category mini-cards (2-per-row wrapping grid with micro bars)
 *
 * Fixes:
 *   - Category labels never truncate (full names inside cards)
 *   - No donut — replaced with compact progress ribbon
 *   - Scales cleanly from 3 to 12+ categories via wrapping cards
 */

import React, { useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { PreflightItem, PreflightSummary, PreflightState, ResourcePhase } from '../../Data/PreFlight/preflightTypes';
import { PREFLIGHT_STATES, PREFLIGHT_CATEGORIES, RESOURCE_PHASES, computePreflightSummary } from '../../Data/PreFlight/preflightTypes';
import styles from './ReadinessDashboard.module.css';

/* ------------------------------------------------------------------ */
/*  Config                                                             */
/* ------------------------------------------------------------------ */

const STATE_COLORS: Record<PreflightState, string> = {
  draft: 'var(--text-muted)',
  seeking: 'var(--info-text)',
  blocked: 'var(--color-error)',
  partial: 'var(--color-warning)',
  met: 'var(--color-success)',
  withdrawn: 'var(--text-disabled, var(--text-muted))',
};

const CATEGORY_ICONS: Record<string, string> = {
  materials: '\u{1F4E6}',
  equipment: '\u{1F527}',
  services: '\u{1F91D}',
  labor: '\u{1F477}',
  transportation: '\u{1F68C}',
  facilities: '\u{1F3E2}',
  admin_legal: '\u{1F4CB}',
  knowledge: '\u{1F4DA}',
  financial: '\u{1F4B0}',
  in_kind: '\u{1F381}',
  software_tech: '\u{1F4BB}',
  other: '\u{1F4CE}',
  // Legacy fallback keys
  tools_equipment: '\u{1F527}',
  administrative: '\u{1F4CB}',
  land_site: '\u{1F3E2}',
};

const PHASE_ICONS: Record<ResourcePhase, string> = {
  preparation: '\u{1F4CB}',
  operational: '\u{2699}\uFE0F',
  sustainment: '\u{1F331}',
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ReadinessDashboardProps {
  items: PreflightItem[];
  onFilterByState?: (state: PreflightState) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ReadinessDashboard({ items, onFilterByState }: ReadinessDashboardProps) {
  const { t } = useAppTranslation('kpi');

  const summary: PreflightSummary = useMemo(() => computePreflightSummary(items), [items]);

  const activeStates = useMemo(() => {
    return PREFLIGHT_STATES.filter(s => s !== 'withdrawn' && summary.by_state[s] > 0);
  }, [summary]);

  const activePhases = useMemo(() => {
    return RESOURCE_PHASES.filter(p => summary.by_phase[p]?.total > 0);
  }, [summary]);

  const activeCategories = useMemo(() => {
    return PREFLIGHT_CATEGORIES.filter(c => summary.by_category[c]?.total > 0);
  }, [summary]);

  const priorityCounts = useMemo(() => {
    const counts = { critical: 0, important: 0, nice_to_have: 0 };
    for (const item of items) {
      if (item.state === 'withdrawn') continue;
      if (counts[item.priority] !== undefined) counts[item.priority]++;
    }
    return counts;
  }, [items]);

  /** Estimated total cost from all active items.
   *  Prefer the API-computed estimated_total; fall back to qty × unit_cost. */
  const estimatedCost = useMemo(() => {
    return items.reduce((sum, i) => {
      if (i.state === 'withdrawn') return sum;
      if (i.estimated_total != null) return sum + i.estimated_total;
      if (i.unit_cost != null) return sum + (i.target_quantity ?? 1) * i.unit_cost;
      return sum;
    }, 0);
  }, [items]);

  const pct = summary.readiness_pct;
  const hasBlocked = summary.by_state.blocked > 0;

  /* Color semantics: blue=complete, red=blocked, yellow=in-progress, muted=0% */
  const progressColor = hasBlocked
    ? 'var(--color-error)'
    : pct === 100
      ? 'var(--info-text)'
      : pct === 0
        ? 'var(--text-muted)'
        : 'var(--color-warning)';

  return (
    <div className={styles.dashboard}>
      {/* ── Row 1: Percentage + progress bar + cost badge ───── */}
      <div className={styles.ribbonRow}>
        <div className={styles.pctBadge} style={{ color: progressColor }}>
          {pct}<span className={styles.pctSign}>%</span>
        </div>

        <div className={styles.barGroup}>
          <div className={styles.barTrack}>
            <div
              className={styles.barFill}
              style={{ width: `${Math.max(pct, 1)}%`, background: progressColor }}
            />
          </div>
          <div className={styles.barMeta}>
            <span className={styles.barMetaText}>
              {t('preflight.fulfilled', {
                count: String(summary.fulfilled_items),
                total: String(summary.total_items),
              })}
            </span>
            {hasBlocked && (
              <span className={styles.barMetaBlocked}>
                {summary.by_state.blocked} {t('preflight.blocked')}
              </span>
            )}
          </div>
        </div>

        {estimatedCost > 0 && (
          <div className={styles.costBadge}>
            {t('preflight.estimatedCostBadge', {
              cost: estimatedCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            })}
          </div>
        )}
      </div>

      {/* ── Row 2: State chips + priority badges ────────────── */}
      <div className={styles.stateRow}>
        <div className={styles.stateChips}>
          {activeStates.map(s => (
            <button
              key={s}
              type="button"
              className={styles.stateChip}
              style={{ '--chip-color': STATE_COLORS[s] } as React.CSSProperties}
              onClick={() => onFilterByState?.(s)}
            >
              <span className={styles.chipDot} />
              <span className={styles.chipLabel}>{t(`preflight.states.${s}`)}</span>
              <span className={styles.chipCount}>{summary.by_state[s]}</span>
            </button>
          ))}
        </div>
        <div className={styles.priorityRow}>
          {priorityCounts.critical > 0 && (
            <span className={`${styles.priorityBadge} ${styles.priorityCritical}`}>
              {t('preflight.priorities.critical')} {priorityCounts.critical}
            </span>
          )}
          {priorityCounts.important > 0 && (
            <span className={`${styles.priorityBadge} ${styles.priorityImportant}`}>
              {t('preflight.priorities.important')} {priorityCounts.important}
            </span>
          )}
          {priorityCounts.nice_to_have > 0 && (
            <span className={`${styles.priorityBadge} ${styles.priorityNice}`}>
              {t('preflight.priorities.nice_to_have')} {priorityCounts.nice_to_have}
            </span>
          )}
        </div>
      </div>

      {/* ── Row 3: Phase pills ──────────────────────────────── */}
      {activePhases.length > 0 && (
        <div className={styles.phaseSection}>
          <span className={styles.sectionLabel}>{t('preflight.phase')}</span>
          <div className={styles.phasePills}>
            {activePhases.map(phase => {
              const d = summary.by_phase[phase];
              const phasePct = d.total > 0 ? Math.round((d.fulfilled / d.total) * 100) : 0;
              return (
                <div key={phase} className={styles.phasePill}>
                  <span className={styles.phaseIcon}>{PHASE_ICONS[phase]}</span>
                  <div className={styles.phaseInfo}>
                    <span className={styles.phaseName}>{t(`preflight.phases.${phase}`)}</span>
                    <div className={styles.phaseTrack}>
                      <div
                        className={`${styles.phaseFill} ${phasePct === 100 ? styles.phaseFillComplete : styles.phaseFillPartial}`}
                        style={{ width: `${phasePct}%` }}
                      />
                    </div>
                  </div>
                  <span className={styles.phaseCount}>{d.fulfilled}/{d.total}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Row 4: Category cards (2-per-row wrapping) ──────── */}
      {activeCategories.length > 0 && (
        <div className={styles.categorySection}>
          <span className={styles.sectionLabel}>{t('preflight.category')}</span>
          <div className={styles.categoryCards}>
            {activeCategories.map(cat => {
              const d = summary.by_category[cat];
              const catPct = d.total > 0 ? Math.round((d.fulfilled / d.total) * 100) : 0;
              return (
                <div key={cat} className={styles.catCard}>
                  <span className={styles.catIcon}>{CATEGORY_ICONS[cat]}</span>
                  <div className={styles.catBody}>
                    <div className={styles.catHeader}>
                      <span className={styles.catName}>{t(`preflight.categories.${cat}`)}</span>
                      <span className={styles.catCount}>{d.fulfilled}/{d.total}</span>
                    </div>
                    <div className={styles.catTrack}>
                      <div
                        className={`${styles.catFill} ${catPct === 100 ? styles.catFillComplete : styles.catFillPartial}`}
                        style={{ width: `${catPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
